import { useEffect, useRef } from 'react'
import { memberAfterRoleChange, MemberProposal } from '../proposedMember'
import useMemberProposal from './useMemberProposal'

interface Params {
  // Creation only: an existing project is never reassigned behind one's back.
  enabled: boolean
  circleId: string | undefined
  memberId: string | null | undefined
  // True once the person picked or cleared the member by hand.
  isMemberDirty: boolean
  setMemberId(memberId: string | null): void
}

interface Result {
  proposal: MemberProposal | undefined
  // The member shown is the one this hook proposed.
  isProposed: boolean
}

// #123: fills the member of a new project when its role has a single holder.
// It never overwrites a member someone chose. A default the screen passed (the
// dashboard's "my projects" assigns oneself) stays only while the chosen role
// counts that person among its holders (#149) — the rule is
// `memberAfterRoleChange`.
export default function useAutoProposeMember({
  enabled,
  circleId,
  memberId,
  isMemberDirty,
  setMemberId,
}: Params): Result {
  const roleHolders = useMemberProposal(enabled ? circleId : undefined)
  const proposal = roleHolders?.proposal
  const lastProposedRef = useRef<string | null>(null)

  useEffect(() => {
    if (!enabled) return
    const current = memberId ?? null
    if (!roleHolders) {
      // No role: only this hook's own proposal goes, a default stays.
      if (
        !isMemberDirty &&
        current !== null &&
        current === lastProposedRef.current
      ) {
        lastProposedRef.current = null
        setMemberId(null)
      }
      return
    }
    const next = memberAfterRoleChange({
      current,
      pickedByHand: isMemberDirty,
      lastProposed: lastProposedRef.current,
      holderIds: roleHolders.holderIds,
      proposal: roleHolders.proposal,
    })
    if (next === current) return
    lastProposedRef.current = next
    setMemberId(next)
  }, [enabled, roleHolders])

  return {
    proposal,
    isProposed: !!memberId && memberId === lastProposedRef.current,
  }
}
